// -----------------------------------------------------------------------------
// Anaglyphic, Netherlands @ 2023.
// See README in the root project for more information.
// -----------------------------------------------------------------------------

import { defineStore } from "pinia";
import { Toast } from "@/stores/toast";
import { upsertLoadingToast, replaceWithTerminalToast } from "@/stores/progressToast";
import router from "@/router/router";
import PriceListImportStatus = Modules.BookingExperts.Enums.PriceListImportStatus;
import ChangedPriceRangeDTO = Modules.BookingExperts.Data.MasterPriceList.ChangedPriceRangeDTO;

// -----------------------------------------------------------------------------

export type PriceListImportProgressEvent = {
	id: number;
	master_price_list_id: number;
	price_list_name: string | null;
	status: PriceListImportStatus;
	current_step: number | null;
	step_count: number | null;
	step_name: string | null;
	imported_count: number;
	changed_price_count: number;
	changed_price_ranges: ChangedPriceRangeDTO[];
	error_message: string | null;
};

interface PriceListImportStoreState {
	progressByPriceListId: Record<string, PriceListImportProgressEvent>;
}

// -----------------------------------------------------------------------------

export const usePriceListImportStore = defineStore({
	id: "priceListImportStore",
	state: (): PriceListImportStoreState => ({
		progressByPriceListId: {},
	}),
	getters: {
		isImporting: (state) => {
			return (priceListId: number): boolean => {
				const progress = state.progressByPriceListId[priceListId.toString()];

				if (!progress) {
					return false;
				}

				return !completedStatuses.includes(progress.status);
			};
		},
		latestImport: (state) => {
			return (priceListId: number): PriceListImportProgressEvent | undefined =>
				state.progressByPriceListId[priceListId.toString()];
		},
	},
	actions: {
		applyProgress(event: PriceListImportProgressEvent) {
			this.progressByPriceListId[event.master_price_list_id.toString()] = event;
		},
		clear(priceListId: number) {
			delete this.progressByPriceListId[priceListId.toString()];
		},
	},
});

// -----------------------------------------------------------------------------

const completedStatuses: PriceListImportStatus[] = [
	"success",
	"failed",
	"skipped",
];

export const handlePriceListImportProgressEvent = (
	event: PriceListImportProgressEvent
) => {
	usePriceListImportStore().applyProgress(event);

	const loadingId = `price-list-import-${event.master_price_list_id}`;
	const terminalId = `price-list-import-${event.master_price_list_id}-done`;

	const priceListName = event.price_list_name ?? "this price list";

	const openImport = () => {
		router.push({
			name: "pricing.imports.view",
			params: { id: event.master_price_list_id, importId: event.id },
		});
	};

	/** "Importing prices · 3 of 12" — the step vocabulary comes from the backend. */
	const humanStep = (): string => {
		const label = event.step_name?.replaceAll("_", " ") ?? "Preparing";

		if (event.current_step === null) {
			return label;
		}

		if (event.step_count === null) {
			return `${label} · page ${event.current_step}`;
		}

		return `${label} · ${event.current_step} of ${event.step_count}`;
	};

	const totals = (): string =>
		`${event.imported_count} prices imported · ${event.changed_price_count} changed`;

	const showLoading = (title: string) =>
		upsertLoadingToast(
			loadingId,
			{ title, message: humanStep() },
			{ onClick: openImport }
		);

	const showTerminal = (
		data: Pick<Toast, "type" | "title" | "message"> & Pick<Toast, "timeout">
	) =>
		replaceWithTerminalToast(loadingId, terminalId, data, {
			onClick: openImport,
		});

	const present: Record<PriceListImportStatus, () => void> = {
		pending: () => showLoading(`Importing ${priceListName}`),
		running: () => showLoading(`Importing ${priceListName}`),
		success: () =>
			showTerminal({
				type: "success",
				title: "Prices imported",
				message: `${totals()} - Click to view the full range list.`,
			}),
		failed: () =>
			showTerminal({
				type: "error",
				title: "Import failed",
				message:
					event.error_message ??
					`Something went wrong while importing prices for ${priceListName}.`,
				timeout: 8000,
			}),
		skipped: () =>
			showTerminal({
				type: "info",
				title: "Nothing imported",
				message:
					event.error_message ??
					`No prices were imported for ${priceListName}.`,
				timeout: 6000,
			}),
	};

	present[event.status]?.();
};
